type SseBlockParseResult = { complete: string[]; remaining: string; };

const SSE_DATA_PREFIX = 'data:';
const SSE_DONE_BLOCK = `${SSE_DATA_PREFIX} [DONE]`;

/**
 * Parses a byte `ReadableStream` of OpenAI-style SSE chunks.
 *
 * Wire format is `data: <json>\n\n` per chunk, ending with `data: [DONE]\n\n`.
 * Yields parsed JSON objects. Stops on `[DONE]` or throws if the stream closes early.
 */
export async function* parseServerSentEvents(stream: ReadableStream<Uint8Array>): AsyncGenerator<unknown> {
  const reader = stream.getReader();
  const decoder = new TextDecoder();
  let buffer = '';

  try {
    for (;;) {
      const { done, value } = await reader.read();
      buffer += decodeStreamChunk(decoder, done, value);

      const blocks = takeCompleteBlocks(buffer);
      buffer = blocks.remaining;

      for (const block of blocks.complete) {
        const parsed = parseSseBlock(block);
        if (parsed.done) {
          return;
        }
        if (parsed.value !== undefined) {
          yield parsed.value;
        }
      }

      if (done) {
        break;
      }
    }

    throw new Error('SSE stream closed before [DONE]');
  } finally {
    reader.releaseLock();
  }
}

function decodeStreamChunk(decoder: TextDecoder, done: boolean, value: Uint8Array | undefined): string {
  if (done) {
    return decoder.decode();
  }
  return decoder.decode(value, { stream: true }).replaceAll('\r\n', '\n');
}

function takeCompleteBlocks(buffer: string): SseBlockParseResult {
  const complete: string[] = [];
  let boundary = buffer.indexOf('\n\n');

  while (boundary !== -1) {
    complete.push(buffer.slice(0, boundary).trim());
    buffer = buffer.slice(boundary + 2);
    boundary = buffer.indexOf('\n\n');
  }

  return { complete, remaining: buffer };
}

function parseSseBlock(block: string): { done: boolean; value?: unknown } {
  if (isDoneLine(block)) {
    return { done: true };
  }

  if (!block.startsWith(SSE_DATA_PREFIX)) {
    return { done: false };
  }

  const data = block.slice(SSE_DATA_PREFIX.length).trimStart();
  return data ? { done: false, value: JSON.parse(data) } : { done: false };
}


function isDoneLine(block: string): boolean {
  return block === SSE_DONE_BLOCK;
}